/**
 * ExclusiveAccessGridBlock
 * ---------------------------------------------------------------------------
 * Static grid of luxury hotel partner logos on a deep teal field.
 * Mirrors the "Exclusive Access / Unlocked with {agent}" section on agent pages,
 * but with configurable kicker + heading for use on the homepage and elsewhere.
 *
 * Logos are curated in-component (not CMS-configurable) to keep parity with
 * agent pages — if partner list changes, both surfaces update together.
 */

import { logoPictureSources, partnerLogoIntrinsicSize } from '@/lib/responsive-image';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';
import { withBlockRegistration } from '../withBlockRegistration';

interface ExclusiveAccessGridBlockProps {
    id?: number | string;
    rawContent?: Record<string, unknown>;
    settings?: Record<string, unknown>;
    isEditing?: boolean;
}

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const BENEFIT_LOGOS = [
    {
        name: 'Four Seasons Preferred Partners',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/four-seasons-preferred-partners-trimmed.png',
        url: '/four-seasons-preferred-partners',
    },
    { name: 'Hyatt Privé', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/hyatt-prive-trimmed.png', url: '/hyatt-prive' },
    {
        name: 'Mandarin Oriental Fan Club',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/mandarin-oriental-fan-club-trimmed.png',
        url: '/mandarin-oriental-fan-club',
    },
    { name: 'Rosewood Elite', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/rosewood-elite-trimmed.png', url: '/rosewood-elite-benefits' },
    { name: 'Peninsula PenClub', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/peninsula-penclub-trimmed.png', url: '/peninsula-penclub' },
    {
        name: 'Belmond Bellini Club',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/belmond-bellini-club-trimmed.png',
        url: '/belmond-bellini-club-',
    },
    {
        name: 'Shangri-La Luxury Circle',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/shangri-la-luxury-circle-trimmed.png',
        url: '/shangri-la-luxury-circle',
    },
    { name: 'Hilton For Luxury', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/hilton-for-luxury-trimmed.png', url: '/hilton-for-luxury' },
    {
        name: 'Jumeirah Passport to Luxury',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/jumeirah-passport-to-luxury-trimmed.png',
        url: '/jumeirah-passport-to-luxury',
    },
    {
        name: 'Couture by Langham',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/couture-by-langham-trimmed.png',
        url: '/couture-by-langham',
    },
    { name: 'IHG Destined', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/ihg-destined-trimmed.png', url: '/ihg-destined' },
    { name: 'Accor Preferred HERA', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/accor-preferred-hera-trimmed.png', url: '/accor-hera' },
    {
        name: 'Platinum Partner',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/platinum-partner-trimmed.png',
        url: '/preferred-hotels-and-resorts-platinum-partner-benefits',
    },
    {
        name: 'Select by Internova',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/select-by-internova-trimmed.png',
        url: '/select-by-internova',
    },
    {
        name: 'ABC\u2022CCRA Premier Hotel Program',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/abcccra-premier-hotel-program-trimmed.png',
        url: '/abc-ccra-premier-hotel-program',
    },
    {
        name: 'Travel Leaders Network',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/travel-leaders-network-trimmed.png',
        url: '/travel-leaders',
    },
    {
        name: 'Noble House VIP Select',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/noble-house-vip-select-trimmed.png',
        url: '/noble-house-vip-select',
    },
    {
        name: 'Trump Preferred Partner',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/trump-preferred-partner-trimmed.png',
        url: '/trump-elite-partners',
    },
];

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
};

const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const ExclusiveAccessGridBlock = React.forwardRef<HTMLElement, ExclusiveAccessGridBlockProps>(({ rawContent = {}, isEditing = false }, ref) => {
    const prefersReducedMotion = useReducedMotion();
    const kicker = (rawContent.kicker as string) || 'Exclusive Access';
    const heading = (rawContent.heading as string) || 'Global Connections';

    const headerMotion = prefersReducedMotion
        ? {}
        : {
              initial: { opacity: 0, y: 12 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true },
              transition: { duration: 0.7, ease: EASE },
          };

    const gridMotion = prefersReducedMotion
        ? {}
        : {
              variants: stagger,
              initial: 'hidden' as const,
              whileInView: 'visible' as const,
              viewport: { once: true, margin: '-40px' },
          };

    const cellMotion = prefersReducedMotion ? {} : { variants: fadeUp };

    return (
        <section ref={ref} className={cn('nav-px w-full bg-[#0e2727] py-16 md:py-20', isEditing && 'ring-1 ring-primary/30')}>
            <div className="mx-auto max-w-[1600px]">
                <motion.div {...headerMotion} className="mb-10 text-center md:mb-14">
                    <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/60">{kicker}</p>
                    <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">{heading}</h2>
                </motion.div>

                <motion.div {...gridMotion} className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {BENEFIT_LOGOS.map((logo) => {
                        const sources = logoPictureSources(logo.logo);
                        const { width, height } = partnerLogoIntrinsicSize(logo.logo);
                        const imgClass = cn(
                            'w-auto object-contain opacity-50 brightness-0 invert transition-opacity duration-200 hover:opacity-80',
                            logo.name.includes('Four Seasons') ? 'h-16 -translate-y-[33px]' : 'h-8',
                        );
                        return (
                            <motion.div key={logo.name} {...cellMotion}>
                                <Link
                                    href={logo.url}
                                    className="flex items-center justify-center rounded-sm px-4 py-5 transition-opacity duration-200 hover:opacity-100"
                                >
                                    {sources ? (
                                        <picture>
                                            <source type="image/avif" srcSet={sources.avif} />
                                            <source type="image/webp" srcSet={sources.webp} />
                                            <img src={logo.logo} alt={logo.name} width={width} height={height} className={imgClass} draggable={false} />
                                        </picture>
                                    ) : (
                                        <img src={logo.logo} alt={logo.name} width={width} height={height} className={imgClass} draggable={false} />
                                    )}
                                </Link>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
});

ExclusiveAccessGridBlock.displayName = 'ExclusiveAccessGridBlock';

export default withBlockRegistration(ExclusiveAccessGridBlock, 'exclusive_access_grid');
